// client/src/components/event/BudgetPanel.jsx
//
// Event budget: expense / income line items with estimated vs actual, plus a
// summary card that folds in live ticket revenue from t_ticket_order.
// Against /api/events/:tenant/:eventId/budget(+/summary).

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const EMPTY = { label: '', category: '', kind: 'expense', estimated_amount: '', actual_amount: '', vendor_id: '', notes: '' };

export default function BudgetPanel({ tenantId, eventId }) {
  const base = `/api/events/${tenantId}/${eventId}/budget`;
  const [lines, setLines] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s, v] = await Promise.all([
        axios.get(base),
        axios.get(`${base}/summary`),
        axios.get(`/api/events/${tenantId}/${eventId}/vendors`).catch(() => ({ data: { vendors: [] } })),
      ]);
      setLines(l.data.lines || []);
      setSummary(s.data.summary);
      setVendors(v.data.vendors || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load budget');
    } finally {
      setLoading(false);
    }
  }, [base, tenantId, eventId]);

  useEffect(() => { load(); }, [load]);

  const money = (n) => `$${Number(n || 0).toFixed(2)}`;

  const save = async () => {
    if (!form.label.trim()) { setError('Label is required'); return; }
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        estimated_amount: Number(form.estimated_amount) || 0,
        actual_amount: Number(form.actual_amount) || 0,
        vendor_id: form.vendor_id ? Number(form.vendor_id) : null,
      };
      if (editing && editing.id) await axios.put(`${base}/${editing.id}`, body);
      else await axios.post(base, body);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this line?')) return;
    try { await axios.delete(`${base}/${id}`); await load(); }
    catch (err) { setError(err?.response?.data?.message || 'Delete failed'); }
  };

  const openNew = (kind) => { setForm({ ...EMPTY, kind }); setEditing({}); };
  const openEdit = (l) => {
    setForm({ ...EMPTY, ...l, estimated_amount: l.estimated_amount, actual_amount: l.actual_amount, vendor_id: l.vendor_id || '' });
    setEditing(l);
  };

  const expenses = lines.filter((l) => l.kind === 'expense');
  const incomes = lines.filter((l) => l.kind === 'income');

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-5">
      <h2 className="text-xl font-semibold">Budget</h2>
      {error && <div className="text-sm text-red-600">{error}</div>}

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SummaryCard label="Est. expense" value={money(summary.estimatedExpense)} />
          <SummaryCard label="Actual expense" value={money(summary.actualExpense)} />
          <SummaryCard label="Ticket revenue" value={money(summary.ticketRevenue)} />
          <SummaryCard
            label="Projected net"
            value={money(summary.projectedNet)}
            tone={summary.projectedNet >= 0 ? 'pos' : 'neg'}
          />
        </div>
      )}

      {loading ? (
        <div className="text-sm opacity-70">Loading…</div>
      ) : (
        <>
          <LineTable title="Expenses" lines={expenses} onAdd={() => openNew('expense')} onEdit={openEdit} onRemove={remove} money={money} />
          <LineTable title="Other income" lines={incomes} onAdd={() => openNew('income')} onEdit={openEdit} onRemove={remove} money={money} />
        </>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative w-full max-w-md bg-white dark:bg-neutral-900 text-black dark:text-white rounded-xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold">{editing.id ? 'Edit line' : `New ${form.kind}`}</h3>
            <input
              placeholder="Label" value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              className="w-full px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm"
            />
            <input
              placeholder="Category" value={form.category || ''}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className="w-full px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm"
            />
            <div className="flex gap-2">
              <label className="flex-1 text-xs opacity-70">Estimated
                <input type="number" step="0.01" value={form.estimated_amount}
                  onChange={(e) => setForm((f) => ({ ...f, estimated_amount: e.target.value }))}
                  className="mt-1 w-full px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm" />
              </label>
              <label className="flex-1 text-xs opacity-70">Actual
                <input type="number" step="0.01" value={form.actual_amount}
                  onChange={(e) => setForm((f) => ({ ...f, actual_amount: e.target.value }))}
                  className="mt-1 w-full px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm" />
              </label>
            </div>
            {form.kind === 'expense' && vendors.length > 0 && (
              <select
                value={form.vendor_id}
                onChange={(e) => setForm((f) => ({ ...f, vendor_id: e.target.value }))}
                className="w-full px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm"
              >
                <option value="">No vendor link</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 rounded border border-black/15 dark:border-white/15 text-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded bg-cobalt text-white text-sm disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  const toneCls = tone === 'pos' ? 'text-emerald-600 dark:text-emerald-400'
    : tone === 'neg' ? 'text-red-600 dark:text-red-400' : '';
  return (
    <div className="rounded-lg border border-black/10 dark:border-white/10 p-3">
      <div className="text-xs opacity-60">{label}</div>
      <div className={`font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

function LineTable({ title, lines, onAdd, onEdit, onRemove, money }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold">{title}</h3>
        <button onClick={onAdd} className="text-sm px-2.5 py-1 rounded border border-black/15 dark:border-white/15">Add</button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-black/10 dark:border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-black/10 dark:border-white/10 opacity-70">
              <th className="p-2">Label</th>
              <th className="p-2">Vendor</th>
              <th className="p-2 text-right">Estimated</th>
              <th className="p-2 text-right">Actual</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} className="border-b border-black/5 dark:border-white/5">
                <td className="p-2">
                  <button onClick={() => onEdit(l)} className="hover:underline underline-offset-2">{l.label}</button>
                  {l.category && <span className="text-xs opacity-50"> · {l.category}</span>}
                </td>
                <td className="p-2 text-xs opacity-70">{l.vendor_name || '—'}</td>
                <td className="p-2 text-right">{money(l.estimated_amount)}</td>
                <td className="p-2 text-right">{money(l.actual_amount)}</td>
                <td className="p-2 text-right"><button onClick={() => onRemove(l.id)} className="text-xs text-red-600">Delete</button></td>
              </tr>
            ))}
            {lines.length === 0 && <tr><td colSpan={5} className="p-3 text-center opacity-60">Nothing yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
